import { Box, Button, Group, Stack } from '@mantine/core'
import { Consts, useGlobalState } from '@opsopti/shared'
import { IconBrandAndroid, IconBrandApple } from '@tabler/icons-react'
import { QRCodeSVG } from 'qrcode.react'

import { EmptyState } from '@/components/EmptyState'
import { theme } from '@/configs/theme'
import * as classes from '@/styles/404Screen.css'

type ForbiddenScreenProps = {
  reason?: string
  showAppLinks?: boolean
}

export function ForbiddenScreen(props: ForbiddenScreenProps) {
  const { authenticationStore } = useGlobalState()

  return (
    <Stack className={classes.root} justify="center">
      <Box className={classes.inner}>
        <FourOhFour className={classes.image} />

        <EmptyState
          size="lg"
          className={classes.content}
          title="Not Authorized"
          description={props.reason ?? 'You are not authorized to view this page.'}
          TitleProps={{ c: 'port' }}
          DescriptionProps={{ c: 'port' }}
          action={{ label: 'Log Out', onClick: () => authenticationStore.logOut() }}
        />

        {props.showAppLinks && (
          <Group gap="xxxl" justify="center">
            <Stack align="center">
              <QRCodeSVG
                value={Consts.iosAppLink!}
                level="L"
                marginSize={1}
                fgColor={theme.colors.blue[9]}
                size={280}
              />
              <Button
                size="sm"
                component={'a'}
                href={Consts.iosAppLink}
                target="_blank"
                color="blue.9"
                leftSection={<IconBrandApple size={22} />}>
                Get from App Store
              </Button>
            </Stack>

            <Stack align="center">
              <QRCodeSVG
                value={Consts.androidAppLink!}
                level="L"
                marginSize={1}
                fgColor={theme.colors.lime[9]}
                size={280}
              />
              <Button
                size="sm"
                component={'a'}
                href={Consts.androidAppLink}
                target="_blank"
                color="lime.9"
                leftSection={<IconBrandAndroid size={22} />}>
                Get from Play Store
              </Button>
            </Stack>
          </Group>
        )}
      </Box>
    </Stack>
  )
}

function FourOhFour(props: React.ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg viewBox="0 0 25 10" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M0.536932 7.55114V5.90625L4.11222 0.272727H5.57386V2.50568H4.72585L2.63352 5.82102V5.8892H7.81108V7.55114H0.536932ZM4.75142 9V7.0483L4.79403 6.32812V0.272727H6.76705V9H4.75142ZM12.5263 9.21307C11.7649 9.21307 11.1087 9.03267 10.5575 8.67188C10.0064 8.30824 9.58168 7.78693 9.28338 7.10795C8.98509 6.42614 8.83736 5.60653 8.8402 4.64915C8.84304 3.69176 8.99219 2.87926 9.28764 2.21165C9.58594 1.54119 10.0092 1.03125 10.5575 0.681818C11.1087 0.329545 11.7649 0.153409 12.5263 0.153409C13.2876 0.153409 13.9439 0.329545 14.495 0.681818C15.049 1.03125 15.4751 1.54119 15.7734 2.21165C16.0717 2.8821 16.2195 3.6946 16.2166 4.64915C16.2166 5.60937 16.0675 6.4304 15.7692 7.11222C15.4709 7.79403 15.0462 8.31534 14.495 8.67614C13.9467 9.03409 13.2905 9.21307 12.5263 9.21307ZM12.5263 7.48722C12.9808 7.48722 13.3487 7.25568 13.63 6.79261C13.9112 6.3267 14.0504 5.61222 14.0476 4.64915C14.0476 4.01847 13.9837 3.49858 13.8558 3.08949C13.728 2.67756 13.5504 2.37074 13.3232 2.16903C13.0959 1.96733 12.8303 1.86648 12.5263 1.86648C12.0746 1.86648 11.7095 2.09517 11.4311 2.55256C11.1527 3.0071 11.0121 3.70597 11.0092 4.64915C11.0064 5.28835 11.0675 5.81818 11.1925 6.23864C11.3203 6.65909 11.4993 6.97301 11.7294 7.1804C11.9595 7.38494 12.2251 7.48722 12.5263 7.48722ZM20.7443 9.11932C20.0795 9.11932 19.4901 9.00568 18.9759 8.77841C18.4645 8.5483 18.0611 8.23153 17.7656 7.82812C17.4702 7.42472 17.3196 6.96023 17.3139 6.43466H19.4361C19.4446 6.625 19.5057 6.79403 19.6193 6.94176C19.733 7.08665 19.8878 7.20028 20.0838 7.28267C20.2798 7.36506 20.5028 7.40625 20.7528 7.40625C21.0028 7.40625 21.223 7.36222 21.4134 7.27415C21.6065 7.18324 21.7571 7.05966 21.8651 6.90341C21.973 6.74432 22.0256 6.5625 22.0227 6.35795C22.0256 6.15341 21.9673 5.97159 21.848 5.8125C21.7287 5.65341 21.5597 5.52983 21.3409 5.44176C21.125 5.35369 20.8693 5.30966 20.5739 5.30966H19.7259V3.80966H20.5739C20.8324 3.80966 21.0597 3.76705 21.2557 3.68182C21.4545 3.59659 21.6094 3.47727 21.7202 3.32386C21.831 3.16761 21.8849 2.98864 21.8821 2.78693C21.8849 2.59091 21.8381 2.41903 21.7415 2.27131C21.6477 2.12074 21.5156 2.00426 21.3452 1.92188C21.1776 1.83949 20.983 1.7983 20.7614 1.7983C20.5284 1.7983 20.3168 1.83949 20.1264 1.92188C19.9389 2.00426 19.7898 2.12074 19.679 2.27131C19.5682 2.42187 19.5099 2.59659 19.5043 2.79545H17.4886C17.4943 2.27557 17.6392 1.81818 17.9233 1.4233C18.2074 1.02557 18.5938 0.714488 19.0824 0.490057C19.5739 0.265625 20.1335 0.153409 20.7614 0.153409C21.3864 0.153409 21.9361 0.262784 22.4105 0.481534C22.8849 0.700284 23.2543 0.998579 23.5185 1.37642C23.7827 1.75142 23.9148 2.17614 23.9148 2.65057C23.9176 3.14205 23.7571 3.54687 23.4332 3.86506C23.1122 4.18324 22.6989 4.37926 22.1932 4.45312V4.52131C22.8693 4.60085 23.3793 4.8196 23.723 5.17756C24.0696 5.53551 24.2415 5.98295 24.2386 6.51989C24.2386 7.02557 24.0895 7.47443 23.7912 7.86648C23.4957 8.25568 23.0838 8.5625 22.5554 8.78693C22.0298 9.00852 21.4261 9.11932 20.7443 9.11932Z"
        fill="currentColor"
      />
    </svg>
  )
}

// const link = isIos
// ? 'https://apps.apple.com/us/app/opsopti/id6740011204'
// : 'https://play.google.com/store/apps/details?id=com.opsopti.app'
